"use client";

import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import type React from "react";
import { useControllableState } from "../hooks/use-controllable-state";
import { useIsMobile } from "../hooks/use-mobile";
import { createContext } from "../lib/create-context";
import { cn } from "../lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "./dialog/popover";
import { Drawer } from "./drawer";

type PrimitiveDivProps = React.ComponentPropsWithoutRef<"div">;
type PrimitiveButtonElement = React.ElementRef<"button">;
type PrimitiveButtonProps = React.ComponentPropsWithoutRef<"button">;

/* ----------------------------------------------------------------------------
 * Component Drover:Root
 * --------------------------------------------------------------------------*/

type DroverContextValue = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isMobile: boolean;
};
export interface DroverProps extends PrimitiveDivProps {
  children?: React.ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?(open: boolean): void;
}

const ROOT_NAME = "Root";

const [DroverProvider, useDroverContext] = createContext<DroverContextValue>(ROOT_NAME);

const Root: React.FC<DroverProps> = (props) => {
  const { open: openProp, defaultOpen, onOpenChange, children } = props;
  // Default to false (desktop) to prevent hydration mismatches and layout shifts
  const isMobile = useIsMobile({ defaultValue: false });
  const [open, setOpen] = useControllableState({
    prop: openProp,
    defaultProp: defaultOpen ?? false,
    onChange: onOpenChange,
    caller: ROOT_NAME,
  });
  const RootComponent = isMobile ? Drawer.Root : Popover;

  return (
    <DroverProvider open={open} onOpenChange={setOpen} isMobile={isMobile}>
      <RootComponent open={open} onOpenChange={setOpen}>
        {children}
      </RootComponent>
    </DroverProvider>
  );
};

Root.displayName = ROOT_NAME;

/* ----------------------------------------------------------------------------
 * Component Drover:Trigger
 * --------------------------------------------------------------------------*/

type TriggerProps = PrimitiveButtonProps & {
  asChild?: boolean;
};
const TRIGGER_NAME = "Trigger";

function Trigger(props: TriggerProps & { ref?: React.Ref<PrimitiveButtonElement> }) {
  const { children, asChild = false, ref, ...triggerProps } = props;
  const { isMobile } = useDroverContext(TRIGGER_NAME);

  // Mobile uses vaul (Radix-style `asChild`); desktop uses Base UI's Popover
  // trigger, which composes via the `render` prop instead.
  if (isMobile) {
    return (
      <Drawer.Trigger ref={ref} asChild={asChild} {...triggerProps}>
        {children}
      </Drawer.Trigger>
    );
  }

  if (asChild) {
    return (
      <PopoverTrigger
        ref={ref}
        nativeButton={false}
        render={children as React.ReactElement}
        {...triggerProps}
      />
    );
  }

  return (
    <PopoverTrigger ref={ref} {...triggerProps}>
      {children}
    </PopoverTrigger>
  );
}

/* ----------------------------------------------------------------------------
 * Component Drover:Content
 * --------------------------------------------------------------------------*/

type PopoverContentProps = React.ComponentPropsWithoutRef<typeof PopoverContent>;
type DrawerContentProps = React.ComponentPropsWithoutRef<typeof Drawer.Content>;

type ContentProps = PopoverContentProps | DrawerContentProps;
type ContentElement =
  | React.ElementRef<typeof PopoverContent>
  | React.ElementRef<typeof Drawer.Content>;
const CONTENT_NAME = "Content";

function Content(props: ContentProps & { ref?: React.Ref<ContentElement> }) {
  const { children, onKeyDown, className, onAnimationEnd, ref, ...contentProps } = props;
  const { isMobile } = useDroverContext(CONTENT_NAME);
  const Component = isMobile ? Drawer.Content : PopoverContent;

  return (
    <Component
      ref={ref}
      className={cn(!isMobile && "min-w-60 p-2", className)}
      align="start"
      onKeyDown={onKeyDown}
      {...contentProps}
    >
      {children}
    </Component>
  );
}

/* ----------------------------------------------------------------------------
 * Component Drover:Close
 * --------------------------------------------------------------------------*/

type CloseProps = PrimitiveButtonProps & {
  asChild?: boolean;
};
const CLOSE_NAME = "Close";

function Close(props: CloseProps & { ref?: React.Ref<PrimitiveButtonElement> }) {
  const { children, asChild = false, ref, ...closeProps } = props;
  const { onOpenChange } = useDroverContext(CLOSE_NAME);

  return useRender({
    ref,
    render: asChild ? (children as React.ReactElement) : undefined,
    defaultTagName: "button",
    props: mergeProps<"button">(
      closeProps as React.ComponentProps<"button">,
      {
        onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          onOpenChange(false);
        },
      },
      asChild ? {} : ({ children } as React.ComponentProps<"button">),
    ),
  });
}

/* ----------------------------------------------------------------------------
 * Component Drover:Nested
 * --------------------------------------------------------------------------*/

type NestedProps = PrimitiveDivProps & {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};
const NESTED_NAME = "Nested";

const Nested: React.FC<NestedProps> = (props) => {
  const { children, open: openProp, onOpenChange, defaultOpen, onDrag, ...nestedProps } = props;
  const {
    isMobile,
    open: rootOpen,
    onOpenChange: onRootOpenChange,
  } = useDroverContext(NESTED_NAME);
  const NestedComponent = isMobile ? Drawer.Nested : Popover;
  const [open, setOpen] = useControllableState({
    prop: openProp,
    defaultProp: defaultOpen ?? false,
    onChange: onOpenChange,
    caller: NESTED_NAME,
  });

  return (
    <NestedComponent
      open={open}
      onOpenChange={setOpen}
      onClose={() => rootOpen && onRootOpenChange(false)}
      {...nestedProps}
    >
      {children}
    </NestedComponent>
  );
};

/* ----------------------------------------------------------------------------
 * Exports
 * --------------------------------------------------------------------------*/

export const Drover = Object.assign(
  {},
  {
    Root,
    Trigger,
    Content,
    Close,
    Nested,
  },
);
